/**
 * Keeps the resizable sidebar's width in step with the signed-in person's
 * saved preference — loaded once and reconciled in (server wins), saved back
 * debounced so a drag doesn't send one request per pixel. Never logged, never
 * offered to the assistant (src/core/preferences).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { orpc } from '@/adapters/rpc/client'

const SAVE_DEBOUNCE_MS = 400

/** The saved value, once — call `setSidebarWidth` from `useSidebar()` with it. */
export function useSidebarWidthPreference(enabled: boolean) {
  return useQuery({
    ...orpc.me.preferences.get.queryOptions({ input: { key: 'sidebarWidth' } }),
    enabled,
    staleTime: 60_000,
    retry: false,
  })
}

/** Debounced save, plus an immediate flush for when the page is about to
 * disappear (a route change or a tab close shouldn't lose the last pixel). */
export function useSaveSidebarWidth() {
  const queryClient = useQueryClient()
  const mutation = useMutation(
    orpc.me.preferences.set.mutationOptions({
      onSuccess: (saved) => {
        queryClient.setQueryData(
          orpc.me.preferences.get.queryKey({ input: { key: 'sidebarWidth' } }),
          { key: saved.key, value: saved.value },
        )
      },
      onError: (error: unknown) => {
        // Silent to the person (CLAUDE.md — this is personal UI state, not a
        // mutation that needs a toast); the width still applies this session.
        console.error('[preferences] could not save sidebar width', error)
      },
    }),
  )
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const mutate = mutation.mutate

  const save = useCallback(
    (px: number) => {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        mutate({ key: 'sidebarWidth', value: px })
      }, SAVE_DEBOUNCE_MS)
    },
    [mutate],
  )

  useEffect(() => () => clearTimeout(timer.current), [])

  return save
}
